/**
 * Every query key in the app, in one place.
 *
 * Invalidation is the reason. `invalidateQueries({ queryKey: keys.audits.all })`
 * only does what it says if nobody spells an audit key by hand elsewhere.
 */
export const keys = {
	modules: {
		all: () => ['reference', 'modules'] as const,
	},
	auth: {
		session: () => ['auth', 'session'] as const,
		vocabulary: () => ['auth', 'vocabulary'] as const,
		registrationOpen: () => ['auth', 'registration-open'] as const,
	},
	audits: {
		all: ['audits'] as const,
		list: (module: string) => ['audits', 'list', module] as const,
		detail: (id: string) => ['audits', id] as const,
		files: (id: string) => ['audits', id, 'files'] as const,
		processing: (id: string) => ['audits', id, 'processing'] as const,
		exceptions: (id: string, filters: string) =>
			['audits', id, 'exceptions', filters] as const,
		reports: (id: string) => ['audits', id, 'reports'] as const,
		coverage: (id: string) => ['audits', id, 'coverage'] as const,
		analyses: (id: string) => ['audits', id, 'analyses'] as const,
		analysisAvailability: (id: string) =>
			['audits', id, 'analysis-availability'] as const,
		analysisDetail: (id: string, slug: string) =>
			['audits', id, 'analyses', slug] as const,
		result: (id: string, slug: string, query: string) =>
			['audits', id, 'result', slug, query] as const,
	},
	analysisRuns: {
		detail: (id: string, page: number) =>
			['analysis-runs', id, page] as const,
	},
	exceptions: {
		detail: (id: string) => ['exceptions', id] as const,
		events: (id: string) => ['exceptions', id, 'events'] as const,
	},
	clients: {
		all: ['clients'] as const,
		/* The module is part of every list's key, so one module's cache entry is
       never shown for another. `all` stays the prefix that invalidates both. */
		list: (module: string) => ['clients', 'list', module] as const,
		files: (clientId: string, module: string) =>
			['clients', clientId, 'files', module] as const,
		/* The filter is part of the key, so each distinct filter is its own cache
       entry and typing back to a previous one is instant rather than a refetch. */
		preview: (clientId: string, module: string, filter: string) =>
			['clients', clientId, 'canonical-rows', module, filter] as const,
	},
	fileGroups: {
		/* Per client, so creating a group in one client never invalidates
       another's list. */
		forClient: (clientId: string, module: string) =>
			['file-groups', 'client', clientId, module] as const,
		/* Every module's groups for one client: the prefix, for invalidating. */
		forClientAnyModule: (clientId: string) =>
			['file-groups', 'client', clientId] as const,
		detail: (groupId: string) => ['file-groups', groupId] as const,
	},
	analyses: {
		catalogue: (module?: string) =>
			['analyses', 'catalogue', module ?? 'default'] as const,
		/* The client + filter are part of the key: each distinct data scope is
       its own cache entry, same reasoning as `clients.preview`. */
		availability: (clientId: string, module: string, filter: string) =>
			['analyses', 'availability', clientId, module, filter] as const,
	},
	intake: {
		queue: (module: string) => ['intake', 'queue', module] as const,
		queueAnyModule: () => ['intake', 'queue'] as const,
	},
	files: {
		all: ['files'] as const,
		detail: (id: string) => ['files', id] as const,
		sample: (id: string) => ['files', id, 'sample'] as const,
		classificationRules: (id: string) =>
			['files', id, 'classification-rules'] as const,
		classificationPreview: (id: string, ruleIds: string) =>
			['files', id, 'classification-preview', ruleIds] as const,
		events: (id: string) => ['files', id, 'events'] as const,
		ingest: (id: string) => ['files', id, 'ingest'] as const,
		columns: (id: string) => ['files', id, 'columns'] as const,
	},
	fileRows: {
		/* `base` is the path prefix, so a file read through an audit and the same
       file read on its own are separate cache entries. */
		all: (base: string) => ['file-rows', base] as const,
		summary: (base: string) => ['file-rows', base, 'summary'] as const,
		page: (base: string, query: string) =>
			['file-rows', base, 'page', query] as const,
		values: (base: string, query: string) =>
			['file-rows', base, 'values', query] as const,
	},
	firm: {
		/* `module` omitted = the server's default; its own cache entry. */
		classificationRules: (status: string, module?: string) =>
			[
				'firm',
				'classification-rules',
				status,
				module ?? 'default',
			] as const,
		aliases: (module?: string) =>
			['firm', 'aliases', module ?? 'default'] as const,
		rulesets: (module?: string) =>
			['firm', 'rulesets', module ?? 'default'] as const,
		/* Per module, so O2C and H2R settings never share a cache entry. */
		analysisSettings: (module?: string) =>
			['firm', 'analysis-settings', module ?? 'default'] as const,
		analysisSettingsAll: ['firm', 'analysis-settings'] as const,
	},
	processingRuns: {
		detail: (id: string) => ['processing-runs', id] as const,
	},
	dashboard: {
		overview: (module: string) =>
			['dashboard', 'overview', module] as const,
	},
} as const;
